import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Divider,
  TextField,
  Button,
  IconButton,
  Avatar,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Grid,
  Tooltip,
  useTheme,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import FilterListIcon from "@mui/icons-material/FilterList";
import SortIcon from "@mui/icons-material/Sort";
import SearchIcon from "@mui/icons-material/Search";
import LabelIcon from "@mui/icons-material/Label";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SchoolIcon from "@mui/icons-material/School";
import SportsIcon from "@mui/icons-material/Sports";
import PersonIcon from "@mui/icons-material/Person";
import WarningIcon from "@mui/icons-material/Warning";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import { format, formatDistanceToNow } from "date-fns";
import api from "../../../../utils/api";
import { useAuth } from "../../../../context/AuthContext";

// Note categories matching the backend schema
const NOTE_CATEGORIES = [
  { value: "general", label: "General" },
  { value: "important", label: "Important" },
  { value: "recruiting", label: "Recruiting" },
  { value: "personal", label: "Personal" },
  { value: "strategy", label: "Strategy" },
  { value: "academic", label: "Academic" },
  { value: "athletic", label: "Athletic" },
  { value: "character", label: "Character" },
  { value: "private", label: "Private" },
  { value: "evaluation", label: "Evaluation" },
  { value: "injury", label: "Injury" },
];

// Note themes
const NOTE_THEMES = [
  { value: "team_notes", label: "Team Notes", icon: <StickyNote2Icon /> },
  {
    value: "coaching_staff_notes",
    label: "Coaching Staff Notes",
    icon: <SportsIcon />,
  },
  {
    value: "recruiting_staff_notes",
    label: "Recruiting Staff Notes",
    icon: <ContactSupportIcon />,
  },
  { value: "visit_notes", label: "Visit Notes", icon: <SchoolIcon /> },
];

// Priority levels
const PRIORITY_LEVELS = [
  { value: "low", label: "Low", icon: "🔵" },
  { value: "medium", label: "Medium", icon: "🟡" },
  { value: "high", label: "High", icon: "🟠" },
  { value: "critical", label: "Critical", icon: "🔴" },
];

const NotesSection = ({ athlete, notes = [], onNoteUpdate }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [anchorEl, setAnchorEl] = useState(null);
  const [noteMenuAnchorEl, setNoteMenuAnchorEl] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localNotes, setLocalNotes] = useState([]);
  const [expandedTheme, setExpandedTheme] = useState("team_notes");
  const [formData, setFormData] = useState({
    id: null,
    text: "",
    category: "general",
    theme: "team_notes",
    priority: "medium",
    pinned: false,
    date: new Date().toISOString(),
    author: {
      id: "1",
      name: "Coach",
      avatar: "",
    },
  });
  const textFieldRef = useRef(null);

  // Load notes from API when component mounts
  useEffect(() => {
    if (athlete?.id || athlete?._id) {
      loadNotes();
    }
  }, [athlete?.id, athlete?._id]);

  // Load notes from API
  const loadNotes = async () => {
    const athleteId = athlete?.id || athlete?._id;
    if (!athleteId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.prospectNotes.getMainNotes(athleteId);
      if (response.success) {
        // Transform API notes to match component format
        const transformedNotes = response.data.map((note) => ({
          id: note._id,
          text: note.text,
          category: note.category,
          theme: note.theme || "team_notes",
          priority: note.priority,
          pinned: note.pinned === true, // Explicitly check for true
          date: note.createdAt,
          author: {
            id: note.createdBy?._id || note.createdBy,
            name: note.createdBy?.name || "Coach",
            avatar: "",
          },
        }));

        // Update local state
        setLocalNotes(transformedNotes);

        // Update parent component if callback exists
        if (onNoteUpdate) {
          onNoteUpdate(transformedNotes);
        }
      } else {
        setError("Failed to load notes");
      }
    } catch (err) {
      console.error("Error loading notes:", err);
      setError("Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  // Use local notes state, fallback to passed notes prop
  const actualNotes =
    localNotes.length > 0 ? localNotes : athlete?.notes || notes || [];

  // Transform athlete notes to match expected format
  const transformedNotes = actualNotes.map((note, index) => {
    // Handle different author formats
    let authorName = "Coach";
    if (typeof note.author === "object" && note.author !== null) {
      authorName = note.author.name || "Coach";
    } else if (typeof note.author === "string") {
      authorName = note.author;
    }

    return {
      id: note._id || note.id || index,
      text: note.text || "",
      category: note.category || "general",
      theme: note.theme || "team_notes",
      priority: note.priority || "medium",
      pinned: note.pinned === true, // Explicitly check for true
      date: note.createdAt || note.date || new Date().toISOString(),
      author: {
        id: note.createdBy || "1",
        name: authorName,
        avatar: "",
      },
    };
  });

  // Group notes by theme
  const notesByTheme = NOTE_THEMES.reduce((acc, themeItem) => {
    acc[themeItem.value] = transformedNotes.filter(
      (note) => note.theme === themeItem.value
    );
    return acc;
  }, {});

  // Handle accordion expansion
  const handleAccordionChange = (theme) => (event, isExpanded) => {
    setExpandedTheme(isExpanded ? theme : false);
  };

  // Handle menu open
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle note menu open
  const handleNoteMenuOpen = (event, note) => {
    event.stopPropagation();
    setSelectedNote(note);
    setNoteMenuAnchorEl(event.currentTarget);
  };

  // Handle note menu close
  const handleNoteMenuClose = () => {
    setNoteMenuAnchorEl(null);
  };

  // Handle add note
  const handleAddNote = (theme = "team_notes") => {
    setEditMode(false);
    setFormData({
      id: null,
      text: "",
      category: "general",
      theme: theme,
      priority: "medium",
      pinned: false,
      date: new Date().toISOString(),
      author: {
        id: "1",
        name: "Coach",
        avatar: "",
      },
    });
    setOpenDialog(true);
  };

  // Handle edit note
  const handleEditNote = () => {
    setEditMode(true);
    setFormData({
      ...selectedNote,
    });
    setOpenDialog(true);
    handleNoteMenuClose();
  };

  // Handle delete note
  const handleDeleteNote = async () => {
    const athleteId = athlete?.id || athlete?._id;
    if (!athleteId || !selectedNote) return;

    if (window.confirm("Are you sure you want to delete this note?")) {
      try {
        const response = await api.prospectNotes.delete(
          athleteId,
          selectedNote.id
        );
        if (response.success) {
          // Reload notes after deletion
          await loadNotes();
        } else {
          setError("Failed to delete note");
        }
      } catch (err) {
        console.error("Error deleting note:", err);
        setError("Failed to delete note");
      }
      handleNoteMenuClose();
    }
  };

  // Handle pin note
  const handlePinNote = async () => {
    const athleteId = athlete?.id || athlete?._id;
    if (!athleteId || !selectedNote) return;

    try {
      const response = await api.prospectNotes.update(
        athleteId,
        selectedNote.id,
        {
          pinned: !selectedNote.pinned,
        }
      );

      if (response.success) {
        // Reload notes after pinning
        await loadNotes();
      } else {
        setError("Failed to update note");
      }
    } catch (err) {
      console.error("Error updating note:", err);
      setError("Failed to update note");
    }
    handleNoteMenuClose();
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Handle form field change
  const handleFormChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Handle form submit
  const handleSubmit = async () => {
    const athleteId = athlete?.id || athlete?._id;
    const userId = user?.id || user?._id;

    if (!formData.text.trim() || !athleteId) {
      return;
    }

    if (!userId) {
      setError("User not authenticated. Please login again.");
      return;
    }

    try {
      if (editMode) {
        // Update existing note
        const response = await api.prospectNotes.update(
          athleteId,
          formData.id,
          {
            text: formData.text,
            category: formData.category,
            theme: formData.theme,
            priority: formData.priority,
          }
        );

        if (response.success) {
          await loadNotes();
          setOpenDialog(false);
        } else {
          setError("Failed to update note");
        }
      } else {
        // Create new note
        const response = await api.prospectNotes.create(athleteId, {
          text: formData.text,
          category: formData.category,
          theme: formData.theme,
          priority: formData.priority,
          source: "main", // Always set source as 'main' for NotesSection
        });

        if (response.success) {
          await loadNotes();
          setOpenDialog(false);
        } else {
          setError("Failed to create note");
        }
      }
    } catch (err) {
      console.error("Error saving note:", err);
      setError("Failed to save note");
    }
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    setCategoryFilter(event.target.value);
    handleMenuClose();
  };

  // Handle sort change
  const handleSortChange = (order) => {
    setSortOrder(order);
    handleMenuClose();
  };

  // Focus on text field when dialog opens
  useEffect(() => {
    if (openDialog && textFieldRef.current) {
      setTimeout(() => {
        textFieldRef.current.focus();
      }, 100);
    }
  }, [openDialog]);

  // Filter and sort notes
  const filterAndSortNotes = (notes) => {
    return (
      [...notes]
        // Filter by search term
        .filter(
          (note) =>
            note.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
            note.category.toLowerCase().includes(searchTerm.toLowerCase())
        )
        // Filter by category
        .filter(
          (note) => categoryFilter === "all" || note.category === categoryFilter
        )
        // Sort by date or pinned status
        .sort((a, b) => {
          // Always show pinned notes first
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;

          // Then sort by date
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);

          if (sortOrder === "newest") {
            return dateB - dateA;
          } else {
            return dateA - dateB;
          }
        })
    );
  };

  // Get category color
  const getCategoryColor = (category) => {
    switch (category) {
      case "recruiting":
        return theme.palette.primary.main;
      case "performance":
      case "athletic":
        return theme.palette.success.main;
      case "academic":
        return theme.palette.info.main;
      case "character":
        return theme.palette.warning.main;
      case "concern":
      case "important":
        return theme.palette.error.main;
      case "contact":
        return theme.palette.secondary.main;
      case "personal":
        return theme.palette.success.main;
      case "strategy":
        return theme.palette.warning.main;
      case "private":
        return theme.palette.grey[600];
      case "evaluation":
        return theme.palette.info.main;
      case "injury":
        return theme.palette.warning.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "critical":
        return theme.palette.error.main;
      case "high":
        return theme.palette.warning.main;
      case "medium":
        return theme.palette.info.main;
      case "low":
        return theme.palette.success.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // Get unique categories from notes
  const categories = [
    "all",
    ...new Set(transformedNotes.map((note) => note.category)),
  ];

  if (loading) {
    return (
      <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}
        >
          <StickyNote2Icon sx={{ mr: 1 }} color="primary" />
          Notes & Evaluations
        </Typography>

        {/* Mobile view: Stacked controls */}
        <Box
          sx={{
            display: { xs: "flex", sm: "none" },
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <TextField
            placeholder="Search notes..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 0.5, color: "text.secondary" }} />
              ),
              sx: { borderRadius: 4 },
            }}
            fullWidth
          />
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Tooltip title="Filter & Sort">
              <IconButton onClick={handleMenuOpen} sx={{ flexShrink: 0 }}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Desktop view: Horizontal controls */}
        <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center" }}>
          <TextField
            placeholder="Search notes..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 0.5, color: "text.secondary" }} />
              ),
              sx: { borderRadius: 4 },
            }}
            sx={{ mr: 1 }}
          />
          <Tooltip title="Filter & Sort">
            <IconButton onClick={handleMenuOpen}>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Pinned Notes Section */}
      {(() => {
        const pinnedNotes = transformedNotes.filter((note) => note.pinned);
        if (pinnedNotes.length === 0) return null;

        return (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: "medium", color: "primary.main", mb: 2 }}
            >
              📌 Pinned Notes ({pinnedNotes.length})
            </Typography>
            <Paper
              elevation={0}
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "white",
                borderRadius: 2,
                border: `2px solid ${theme.palette.warning.light}`,
              }}
            >
              <Grid container spacing={2}>
                {pinnedNotes.map((note) => (
                  <Grid item xs={12} sm={6} md={4} key={note.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        borderLeft: `4px solid ${getCategoryColor(
                          note.category
                        )}`,
                        bgcolor: "rgba(255, 235, 59, 0.05)",
                        borderColor: theme.palette.warning.light,
                        "&:hover": {
                          boxShadow: theme.shadows[4],
                        },
                      }}
                    >
                      <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                        <PushPinIcon fontSize="small" color="warning" />
                      </Box>
                      <CardHeader
                        avatar={
                          <Avatar
                            src={note.author.avatar}
                            alt={note.author.name}
                            sx={{ bgcolor: getCategoryColor(note.category) }}
                          >
                            {note.author.name.charAt(0)}
                          </Avatar>
                        }
                        title={note.author.name}
                        subheader={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDistanceToNow(new Date(note.date), {
                                addSuffix: true,
                              })}
                            </Typography>
                            <Chip
                              label={
                                note.category.charAt(0).toUpperCase() +
                                note.category.slice(1)
                              }
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                bgcolor: `${getCategoryColor(note.category)}20`,
                                color: getCategoryColor(note.category),
                              }}
                            />
                            <Chip
                              label={note.theme
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                bgcolor: `${theme.palette.primary.main}20`,
                                color: theme.palette.primary.main,
                              }}
                            />
                            {note.priority && (
                              <Chip
                                label={note.priority}
                                size="small"
                                icon={<PriorityHighIcon />}
                                sx={{
                                  height: 20,
                                  fontSize: "0.7rem",
                                  bgcolor: `${getPriorityColor(
                                    note.priority
                                  )}20`,
                                  color: getPriorityColor(note.priority),
                                }}
                              />
                            )}
                          </Box>
                        }
                        action={
                          <IconButton
                            aria-label="settings"
                            onClick={(e) => handleNoteMenuOpen(e, note)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        }
                      />
                      <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {note.text}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ justifyContent: "flex-end" }}>
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(note.date), "MMM d, yyyy")}
                        </Typography>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Box>
        );
      })()}

      {/* Notes Accordion */}
      {NOTE_THEMES.map((themeItem) => {
        const themeNotes = notesByTheme[themeItem.value] || [];
        const filteredNotes = filterAndSortNotes(themeNotes);
        const hasNotes = filteredNotes.length > 0;

        return (
          <Accordion
            key={themeItem.value}
            expanded={expandedTheme === themeItem.value}
            onChange={handleAccordionChange(themeItem.value)}
            sx={{ mb: 2 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: theme.palette.grey[50],
                "&:hover": {
                  backgroundColor: theme.palette.grey[100],
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {themeItem.icon}
                <Typography variant="h6" sx={{ fontWeight: "medium" }}>
                  {themeItem.label}
                </Typography>
                <Chip
                  label={filteredNotes.length}
                  size="small"
                  color={hasNotes ? "primary" : "default"}
                  sx={{ ml: 1 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddNote(themeItem.value)}
                  size="small"
                >
                  Add {themeItem.label}
                </Button>
              </Box>

              {hasNotes ? (
                <Grid container spacing={2}>
                  {filteredNotes.map((note) => (
                    <Grid item xs={12} sm={6} md={4} key={note.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          borderLeft: `4px solid ${getCategoryColor(
                            note.category
                          )}`,
                          ...(note.pinned && {
                            bgcolor: "rgba(255, 235, 59, 0.05)",
                            borderColor: theme.palette.warning.light,
                          }),
                        }}
                      >
                        {note.pinned && (
                          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                            <PushPinIcon fontSize="small" color="warning" />
                          </Box>
                        )}
                        <CardHeader
                          avatar={
                            <Avatar
                              src={note.author.avatar}
                              alt={note.author.name}
                              sx={{ bgcolor: getCategoryColor(note.category) }}
                            >
                              {note.author.name.charAt(0)}
                            </Avatar>
                          }
                          title={note.author.name}
                          subheader={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatDistanceToNow(new Date(note.date), {
                                  addSuffix: true,
                                })}
                              </Typography>
                              <Chip
                                label={
                                  note.category.charAt(0).toUpperCase() +
                                  note.category.slice(1)
                                }
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: "0.7rem",
                                  bgcolor: `${getCategoryColor(
                                    note.category
                                  )}20`,
                                  color: getCategoryColor(note.category),
                                }}
                              />
                              {note.priority && (
                                <Chip
                                  label={note.priority}
                                  size="small"
                                  icon={<PriorityHighIcon />}
                                  sx={{
                                    height: 20,
                                    fontSize: "0.7rem",
                                    bgcolor: `${getPriorityColor(
                                      note.priority
                                    )}20`,
                                    color: getPriorityColor(note.priority),
                                  }}
                                />
                              )}
                            </Box>
                          }
                          action={
                            <IconButton
                              aria-label="settings"
                              onClick={(e) => handleNoteMenuOpen(e, note)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          }
                        />
                        <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {note.text}
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ justifyContent: "flex-end" }}>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(note.date), "MMM d, yyyy")}
                          </Typography>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <StickyNote2Icon
                    sx={{ fontSize: 60, color: "text.disabled", mb: 2 }}
                  />
                  <Typography variant="h6" gutterBottom>
                    No {themeItem.label} Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {searchTerm || categoryFilter !== "all"
                      ? "Try changing your search or filter settings."
                      : `Start adding ${themeItem.label.toLowerCase()} to track important information about this athlete.`}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddNote(themeItem.value)}
                  >
                    Add First {themeItem.label}
                  </Button>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Filter & Sort Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem disabled>
          <ListItemIcon>
            <FilterListIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Filter by Category" />
        </MenuItem>
        <Divider />
        {categories.map((category) => (
          <MenuItem
            key={category}
            onClick={() => setCategoryFilter(category)}
            selected={categoryFilter === category}
          >
            {category === "all"
              ? "All Categories"
              : category.charAt(0).toUpperCase() + category.slice(1)}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem disabled>
          <ListItemIcon>
            <SortIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Sort by" />
        </MenuItem>
        <MenuItem
          onClick={() => handleSortChange("newest")}
          selected={sortOrder === "newest"}
        >
          Newest First
        </MenuItem>
        <MenuItem
          onClick={() => handleSortChange("oldest")}
          selected={sortOrder === "oldest"}
        >
          Oldest First
        </MenuItem>
      </Menu>

      {/* Note Menu */}
      <Menu
        anchorEl={noteMenuAnchorEl}
        open={Boolean(noteMenuAnchorEl)}
        onClose={handleNoteMenuClose}
      >
        <MenuItem onClick={handleEditNote}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Edit Note" />
        </MenuItem>
        <MenuItem onClick={handlePinNote}>
          <ListItemIcon>
            {selectedNote?.pinned ? (
              <PushPinOutlinedIcon fontSize="small" />
            ) : (
              <PushPinIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={selectedNote?.pinned ? "Unpin Note" : "Pin Note"}
          />
        </MenuItem>
        <MenuItem onClick={handleDeleteNote}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete Note" />
        </MenuItem>
      </Menu>

      {/* Add/Edit Note Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editMode ? "Edit Note" : "Add New Note"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="note-text"
            label="Note"
            type="text"
            fullWidth
            multiline
            rows={6}
            value={formData.text}
            onChange={(e) => handleFormChange("text", e.target.value)}
            inputRef={textFieldRef}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleFormChange("category", e.target.value)}
                >
                  {NOTE_CATEGORIES.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Theme</InputLabel>
                <Select
                  value={formData.theme}
                  label="Theme"
                  onChange={(e) => handleFormChange("theme", e.target.value)}
                >
                  {NOTE_THEMES.map((themeItem) => (
                    <MenuItem key={themeItem.value} value={themeItem.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {themeItem.icon}
                        {themeItem.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => handleFormChange("priority", e.target.value)}
                >
                  {PRIORITY_LEVELS.map((priority) => (
                    <MenuItem key={priority.value} value={priority.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <span>{priority.icon}</span>
                        {priority.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Box
                  sx={{ display: "flex", alignItems: "center", height: "100%" }}
                >
                  <Button
                    variant={formData.pinned ? "contained" : "outlined"}
                    startIcon={
                      formData.pinned ? (
                        <PushPinIcon />
                      ) : (
                        <PushPinOutlinedIcon />
                      )
                    }
                    onClick={() => handleFormChange("pinned", !formData.pinned)}
                    color={formData.pinned ? "warning" : "primary"}
                  >
                    {formData.pinned ? "Pinned" : "Pin Note"}
                  </Button>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.text.trim()}
          >
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default NotesSection;
